import { Avatar, AvatarFallback, AvatarImage, Badge } from "@aircall/ds";
import { AiAgentsFilled } from "@aircall/react-icons";
import { useTranslation } from "react-i18next";

import { Section, SectionHeader, SectionTitle } from "@/blocks/Section/Section";
import { AIActionItem } from "@/helpers/aiActions/aiActions.helpers.decl";
import { formatSecondsToTime } from "@/helpers/date/date.helpers";
import { useCallAIActions } from "@/hooks/useCallAIActions/useCallAIActions";

type AIActionRowProps = {
  action: AIActionItem;
};

function AIActionRow({ action }: AIActionRowProps) {
  const { t } = useTranslation();

  return (
    <li className="flex items-center gap-2" data-test="ai-actions-section-item">
      <Avatar size="sm">
        {action.iconUrl && <AvatarImage src={action.iconUrl} alt="" />}
        <AvatarFallback
          className={action.isSuccess ? "bg-success" : "bg-destructive"}
        >
          <AiAgentsFilled className="size-4 text-primary-foreground" />
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm">{action.actionName}</span>
        {action.applicationName && (
          <span className="truncate text-xs text-muted-foreground">
            {action.applicationName}
          </span>
        )}
      </div>
      <Badge color={action.isSuccess ? "green" : "red"} tone="light">
        {t(
          action.isSuccess
            ? "ai_action.status.success"
            : "ai_action.status.failure"
        )}
      </Badge>
      <span className="ml-auto text-xs text-muted-foreground">
        {formatSecondsToTime(action.startTime)}
      </span>
    </li>
  );
}

export function SectionAIActions() {
  const { t } = useTranslation();
  const aiActions = useCallAIActions();

  if (aiActions.length === 0) {
    return null;
  }

  return (
    <Section data-test="ai-actions-section">
      <SectionHeader>
        <SectionTitle>{t("call_context.ai_actions")}</SectionTitle>
      </SectionHeader>
      <ul className="flex flex-col gap-2">
        {aiActions.map((action) => (
          <AIActionRow key={action.id} action={action} />
        ))}
      </ul>
    </Section>
  );
}
